import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { type KeyboardEvent, useId, useRef, useState } from "react";
import {
  type Reservation,
  useCancelReservation,
  useCreateReservation,
} from "../../hooks/api/useReservations";
import type { User as AdminUser } from "../../hooks/api/useUsers";
import { ApiError } from "../../lib/api-client";
import { useAuthStore } from "../../stores/authStore";
import { useToastStore } from "../../stores/toastStore";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { RecurringReservationForm } from "./RecurringReservationForm";
import { UserSelectCombobox } from "./UserSelectCombobox";

type TabType = "single" | "recurring";
export type ModalMode = "own" | "other" | "new";

const TABS: readonly TabType[] = ["single", "recurring"] as const;

interface SeatDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  seatId: number;
  seatLabel: string;
  date: string;
  reservation?: Reservation;
}

const TAB_BASE_STYLES = [
  "flex-1 px-4 py-2 text-sm font-medium text-center",
  "border-b-2 transition-colors duration-200",
  "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset",
].join(" ");

const TAB_ACTIVE_STYLES = "border-primary-600 text-primary-600";
const TAB_INACTIVE_STYLES =
  "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";

function getTabClassName(isActive: boolean): string {
  return `${TAB_BASE_STYLES} ${isActive ? TAB_ACTIVE_STYLES : TAB_INACTIVE_STYLES}`;
}

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    // API 側の packages/api/src/types/error.ts の ErrorCodes に対応する。
    // 基本は API が返す message を表示し、空のときだけ UI 用フォールバックを使う。
    // フォールバックは「同日重複 / 座席重複」両方を含むため特定原因を断定しない曖昧表現にする。
    switch (err.code) {
      case "CONFLICT":
        return err.message || "予約が競合しています";
      case "AUTHORIZATION_ERROR":
        return err.message || "この操作を行う権限がありません";
      case "NOT_FOUND":
        return err.message || "対象が見つかりません";
      default:
        return err.message || fallback;
    }
  }
  return err instanceof Error ? err.message || fallback : fallback;
}

function formatDisplayDate(date: string): string {
  return format(parseISO(date), "yyyy年M月d日（E）", { locale: ja });
}

function resolveMode(reservation: Reservation | undefined): ModalMode {
  if (!reservation) {
    return "new";
  }
  return reservation.isOwn ? "own" : "other";
}

export function SeatDetailModal({
  isOpen,
  onClose,
  seatId,
  seatLabel,
  date,
  reservation,
}: SeatDetailModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("single");
  const [proxyUser, setProxyUser] = useState<AdminUser | null>(null);
  const createReservation = useCreateReservation();
  const cancelReservation = useCancelReservation();
  const addToast = useToastStore((s) => s.addToast);
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.isAdmin === true;

  const isLoading = createReservation.isPending || cancelReservation.isPending;
  const mode: ModalMode = resolveMode(reservation);
  const formattedDate = formatDisplayDate(date);

  const handleClose = () => {
    setError(null);
    setActiveTab("single");
    setProxyUser(null);
    onClose();
  };

  const handleReserve = async () => {
    setError(null);
    try {
      await createReservation.mutateAsync({
        date,
        seatId,
        ...(proxyUser ? { targetUserEmail: proxyUser.email } : {}),
      });
      const targetLabel = proxyUser ? `（${proxyUser.name}さん名義）` : "";
      addToast(`${seatLabel}を予約しました${targetLabel}`);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, "予約に失敗しました"));
    }
  };

  const handleCancel = async () => {
    setError(null);
    try {
      await cancelReservation.mutateAsync({ date, seatId });
      const targetLabel =
        mode === "other" && reservation
          ? `（${reservation.userName}さんの予約）`
          : "";
      addToast(`${seatLabel}の予約をキャンセルしました${targetLabel}`);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, "キャンセルに失敗しました"));
    }
  };

  if (mode !== "new") {
    return (
      <ExistingReservationView
        isOpen={isOpen}
        mode={mode}
        isAdmin={isAdmin}
        seatLabel={seatLabel}
        formattedDate={formattedDate}
        reservation={reservation}
        error={error}
        isLoading={isLoading}
        onClose={handleClose}
        onCancel={handleCancel}
        onDismissError={() => setError(null)}
      />
    );
  }

  return (
    <NewReservationView
      isOpen={isOpen}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      seatId={seatId}
      seatLabel={seatLabel}
      date={date}
      formattedDate={formattedDate}
      error={error}
      onDismissError={() => setError(null)}
      isLoading={isLoading}
      isAdmin={isAdmin}
      proxyUser={proxyUser}
      setProxyUser={setProxyUser}
      currentEmail={currentUser?.email}
      onClose={handleClose}
      onReserve={handleReserve}
    />
  );
}

/* -------------------- Existing Reservation (own / other) -------------------- */

interface ExistingReservationViewProps {
  isOpen: boolean;
  mode: Exclude<ModalMode, "new">;
  isAdmin: boolean;
  seatLabel: string;
  formattedDate: string;
  reservation: Reservation | undefined;
  error: string | null;
  isLoading: boolean;
  onClose: () => void;
  onCancel: () => void;
  onDismissError: () => void;
}

function ExistingReservationView({
  isOpen,
  mode,
  isAdmin,
  seatLabel,
  formattedDate,
  reservation,
  error,
  isLoading,
  onClose,
  onCancel,
  onDismissError,
}: ExistingReservationViewProps) {
  const canProxyCancel = mode === "other" && isAdmin;
  const canCancel = mode === "own" || canProxyCancel;
  const cancelLabel = canProxyCancel ? "代行キャンセル" : "予約をキャンセル";

  const footer = (
    <>
      <Button
        variant="secondary"
        onClick={onClose}
        disabled={canCancel && isLoading}
      >
        閉じる
      </Button>
      {canCancel && (
        <Button
          variant="danger"
          onClick={onCancel}
          isLoading={isLoading}
          disabled={isLoading}
        >
          {cancelLabel}
        </Button>
      )}
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="予約詳細" footer={footer}>
      <div className="space-y-4">
        {canCancel && error && (
          <Alert variant="error" onClose={onDismissError}>
            {error}
          </Alert>
        )}

        {canProxyCancel && (
          <div
            className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-800"
            role="region"
            // i18n-candidate: 将来 i18n 導入時に他のUI文言と一括で外部化する
            aria-label="代行操作の警告"
          >
            管理者として
            {reservation ? `「${reservation.userName}さん」` : ""}
            の予約を代行キャンセルします
          </div>
        )}

        <ReservationDetails
          seatLabel={seatLabel}
          formattedDate={formattedDate}
          reservation={reservation}
        />
      </div>
    </Modal>
  );
}

interface ReservationDetailsProps {
  seatLabel: string;
  formattedDate: string;
  reservation: Reservation | undefined;
}

function ReservationDetails({
  seatLabel,
  formattedDate,
  reservation,
}: ReservationDetailsProps) {
  return (
    <dl className="space-y-3">
      <div className="flex">
        <dt className="w-20 text-gray-500 shrink-0">座席</dt>
        <dd className="text-gray-900 font-medium">{seatLabel}</dd>
      </div>
      <div className="flex">
        <dt className="w-20 text-gray-500 shrink-0">日付</dt>
        <dd className="text-gray-900">{formattedDate}</dd>
      </div>
      {reservation && (
        <>
          <div className="flex">
            <dt className="w-20 text-gray-500 shrink-0">予約者</dt>
            <dd className="text-gray-900">{reservation.userName}</dd>
          </div>
          <div className="flex">
            <dt className="w-20 text-gray-500 shrink-0">予約日時</dt>
            <dd className="text-gray-900">
              {format(parseISO(reservation.createdAt), "yyyy/MM/dd HH:mm")}
            </dd>
          </div>
        </>
      )}
    </dl>
  );
}

/* -------------------- New Reservation (tabs: single / recurring) -------------------- */

interface NewReservationViewProps {
  isOpen: boolean;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  seatId: number;
  seatLabel: string;
  date: string;
  formattedDate: string;
  error: string | null;
  onDismissError: () => void;
  isLoading: boolean;
  isAdmin: boolean;
  proxyUser: AdminUser | null;
  setProxyUser: (user: AdminUser | null) => void;
  currentEmail: string | undefined;
  onClose: () => void;
  onReserve: () => void;
}

function NewReservationView({
  isOpen,
  activeTab,
  setActiveTab,
  seatId,
  seatLabel,
  date,
  formattedDate,
  error,
  onDismissError,
  isLoading,
  isAdmin,
  proxyUser,
  setProxyUser,
  currentEmail,
  onClose,
  onReserve,
}: NewReservationViewProps) {
  const singleTabId = useId();
  const singlePanelId = useId();
  const recurringTabId = useId();
  const recurringPanelId = useId();

  const tabIds: Record<TabType, string> = {
    single: singleTabId,
    recurring: recurringTabId,
  };
  const panelIds: Record<TabType, string> = {
    single: singlePanelId,
    recurring: recurringPanelId,
  };

  const isProxyMode = isAdmin && proxyUser !== null;

  const footer =
    activeTab === "single" ? (
      <>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          閉じる
        </Button>
        <Button
          variant="primary"
          onClick={onReserve}
          isLoading={isLoading}
          disabled={isLoading}
        >
          {isProxyMode ? "代行予約する" : "予約する"}
        </Button>
      </>
    ) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="座席予約" footer={footer}>
      <div className="space-y-4">
        <TabList
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabIds={tabIds}
          panelIds={panelIds}
        />

        {/* WAI-ARIA APG Tabs パターン: tabpanel は tabindex=0 でフォーカス可能にする */}
        <div
          id={singlePanelId}
          role="tabpanel"
          aria-labelledby={singleTabId}
          tabIndex={0}
          hidden={activeTab !== "single"}
        >
          {error && (
            <Alert variant="error" onClose={onDismissError}>
              {error}
            </Alert>
          )}

          <dl className="space-y-3">
            <div className="flex">
              <dt className="w-20 text-gray-500 shrink-0">座席</dt>
              <dd className="text-gray-900 font-medium">{seatLabel}</dd>
            </div>
            <div className="flex">
              <dt className="w-20 text-gray-500 shrink-0">日付</dt>
              <dd className="text-gray-900">{formattedDate}</dd>
            </div>
          </dl>

          {isAdmin && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <UserSelectCombobox
                proxyTarget={proxyUser}
                onChange={setProxyUser}
                excludeEmail={currentEmail}
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        {/* WAI-ARIA APG Tabs パターン: tabpanel は tabindex=0 でフォーカス可能にする */}
        <div
          id={recurringPanelId}
          role="tabpanel"
          aria-labelledby={recurringTabId}
          tabIndex={0}
          hidden={activeTab !== "recurring"}
        >
          {isProxyMode ? (
            <div
              className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-3 text-sm text-warning-800"
              role="region"
              // i18n-candidate: 将来 i18n 導入時に他のUI文言と一括で外部化する
              aria-label="代行モード時の制約"
            >
              繰り返し予約の代行作成は現在サポートされていません。代行予約は通常予約タブからのみ作成できます。
            </div>
          ) : (
            <RecurringReservationForm
              seatId={seatId}
              seatLabel={seatLabel}
              defaultStartDate={date}
              onSuccess={onClose}
              onCancel={onClose}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

interface TabListProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  tabIds: Record<TabType, string>;
  panelIds: Record<TabType, string>;
}

function TabList({ activeTab, setActiveTab, tabIds, panelIds }: TabListProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabKeyDown = (e: KeyboardEvent, currentIndex: number) => {
    let nextIndex: number | null = null;

    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % TABS.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = TABS.length - 1;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      const nextTab = TABS[nextIndex];
      if (nextTab) {
        setActiveTab(nextTab);
        tabRefs.current[nextIndex]?.focus();
      }
    }
  };

  return (
    <div
      className="flex border-b border-gray-200"
      role="tablist"
      aria-label="予約タイプ"
    >
      {TABS.map((tab, index) => {
        const isActive = activeTab === tab;
        const label = tab === "single" ? "通常予約" : "繰り返し予約";
        return (
          <button
            key={tab}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            id={tabIds[tab]}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelIds[tab]}
            tabIndex={isActive ? 0 : -1}
            onClick={() => setActiveTab(tab)}
            onKeyDown={(e) => handleTabKeyDown(e, index)}
            className={getTabClassName(isActive)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
